import { useEffect, useReducer } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import useAxios from "../../../MainComponents/Hooks/useAxios";
import { toast } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";

import QuizHeader from "./component/quizHeader/QuizHeader";
import { DisplayQuestion } from "./component/displayQuestion/DisplayQuestion";
import Question from "./component/displayQuestion/Question";
import QuizFooter from "./component/quizFooter/QuizFooter";
import RightQuestionsSidebar from "./component/quizHeader/RightQuestionsSidebar";
import DisplayAnswers from "./component/displayAnswers/DisplayAnswers";
import QuestionsAnswers from "./component/displayAnswers/QuestionsAnswers";
import PercentageSidebar from "./component/displayAnswers/PercentageSidebar";
import Loader from "../../../MainComponents/Loader";
import HelmetTags from "../../../MainComponents/HelmetTags";
import { useAuthState } from "../../../MainComponents/GlobalContext";

function reducer(state, action) {
  switch (action.type) {
    case "setLoading": {
      return {
        ...state,
        loading: action.payload,
      };
    }
    case "setCollapse": {
      return {
        ...state,
        collapse: !state.collapse,
      };
    }

    case "setQuestions": {
      return {
        ...state,
        questions: action.payload,
      };
    }

    case "setQuizInfo": {
      return {
        ...state,
        quizInfo: action.payload,
      };
    }

    case "setCurrentQuestionIndex": {
      return {
        ...state,
        collapse: false,
        currentQuestionIndex: action.payload,
      };
    }

    case "setIsShowAnswers": {
      return {
        ...state,
        isShowAnswers: true,
      };
    }

    case "setIsSelectedAnswer": {
      return {
        ...state,
        questions: state.questions.map((question) =>
          question.id === action.payload.questionId
            ? {
                ...question,
                selected_answer: action.payload.answer,
                is_selected: action.payload.questionId,
                n: action.payload.n,
              }
            : question
        ),
      };
    }

    case "setAnsweredQuestions": {
      // Check if answer already exists
      const answerExists = state.answeredQuestions.some(
        (answer) => answer.id === action.payload.id
      );

      // not exist
      if (!answerExists) {
        return {
          ...state,
          answeredQuestions: [...state.answeredQuestions, action.payload],
        };
      } else {
        // Answer already exists, don't add
        return {
          ...state,
          answeredQuestions: state.answeredQuestions.map((question) =>
            question.id === action.payload.id
              ? {
                  ...question,
                  order: action.payload.order,
                  id: action.payload.id,
                  n: action.payload.n,
                  answer_id: action.payload.answer_id,
                }
              : question
          ),
        };
      }
    }

    case "setFinishQuizData": {
      return {
        ...state,
        finishQuizData: action.payload,
      };
    }

    case "setSubmitFinishQuiz": {
      return {
        ...state,
        submitFinishQuiz: action.payload,
      };
    }

    case "setUpdateSelectedAnswer": {
      return {
        ...state,
        updateSelectedAnswer: action.payload,
      };
    }

    case "setAllData": {
      return {
        ...initialState,
      };
    }
    case "setRefetch": {
      return {
        ...state,
        refetch: action.payload,
      };
    }

    default:
      throw Error("Unknown action: " + action.type);
  }
}

const initialState = {
  loading: true,
  currentQuestionIndex: 0,
  collapse: false,
  questions: [],
  quizInfo: {},
  answeredQuestions: [],
  finishQuizData: {},
  numOfSelected: 0,
  isShowAnswers: false,
  refetch: false,
  submitFinishQuiz: {
    flag: "",
    dependency: false,
    data: "",
  },
  updateSelectedAnswer: {
    flag: "",
    dependency: false,
    data: "",
  },
};

const QuizNew = ({ homework, exam }) => {
  const { ID } = useParams();

  const AuthState = useAuthState();

  const [state, dispatch] = useReducer(reducer, initialState);

  const checkApi = homework
    ? process.env.REACT_APP_STUDENT_HOMEWORK_API
    : exam
    ? process.env.REACT_APP_STUDENT_TRIAL_EXAM_SHOW_QUESTIONS_API
    : process.env.REACT_APP_STUDENT_QUIZZ_API;

  const checkFinishApi = homework
    ? process.env.REACT_APP_STUDENT_HOMEWORK_SUBMIT_API
    : exam
    ? process.env.REACT_APP_STUDENT_TRIAL_EXAM_SUBMIT_API
    : process.env.REACT_APP_STUDENT_QUIZZ_SUBMIT_API;

  const [quizData, quizErrors, quizLoading] = useAxios(
    `${checkApi}/${ID}`,
    "GET",
    "GET",
    state.refetch,
    ""
  );

  const [finishQuizSuccess, finishErrors, finishLoading] = useAxios(
    checkFinishApi,
    "POST",
    state.submitFinishQuiz.flag,
    state.submitFinishQuiz.dependency,
    state.submitFinishQuiz.data,
    true
  );

  useEffect(() => {
    dispatch({
      type: "setQuestions",
      payload: quizData
        ? [...quizData?.choosequestions, ...quizData?.essayquestions]
        : [],
    });

    dispatch({
      type: "setQuizInfo",
      payload: quizData ? quizData.quiz : {},
    });
    dispatch({ type: "setRefetch", payload: false });
  }, [quizData]);

  useEffect(() => {
    if (state.questions?.length > 0) {
      state.questions
        .filter((question) => question.is_selected === 1)
        .map((question) => {
          const sel = question.selected_answer;
          const answerObj =
            question.answers?.find((a) => String(a.id) === String(sel)) ||
            question.answers?.find((a) => String(a.order) === String(sel));

          const answerId = answerObj ? answerObj.id : null;

          dispatch({
            type: "setAnsweredQuestions",
            payload: {
              id: question.id,
              order: question.selected_answer,
              n: question.n,
              answer_id: answerId,
            },
          });

          return null;
        });
    }
  }, [state.questions?.length]);

  useEffect(() => {
    if (finishQuizSuccess) {
      dispatch({
        type: "setSubmitFinishQuiz",
        payload: {
          flag: "",
          dependency: false,
          data: "",
        },
      });

      dispatch({
        type: "setFinishQuizData",
        payload: finishQuizSuccess.data,
      });
      dispatch({ type: "setIsShowAnswers" });
    }
  }, [finishQuizSuccess]);

  const onFinishQuiz = ({ forced = false }) => {
    if (!forced) {
      // Check if all questions are answered
      const allQuestions = state.questions;

      const firstUnansweredIndex = allQuestions.findIndex(
        (q) => !q.selected_answer
      );

      if (firstUnansweredIndex !== -1) {
        toast.error("من فضلك جاوب على كل الأسئلة قبل التسليم");
        dispatch({
          type: "setCurrentQuestionIndex",
          payload: firstUnansweredIndex,
        });
        return;
      }
    }

    let payload = {
      answers: state.answeredQuestions,
    };

    if (exam) {
      payload.exam_id = ID;
    } else {
      payload.lecture_id = ID;
    }

    dispatch({
      type: "setSubmitFinishQuiz",
      payload: {
        flag: "submitQuiz",
        dependency: !state.submitFinishQuiz.dependency,
        data: payload,
      },
    });
  };

  return (
    <section
      className={`flex h-fit  min-h-screen w-full flex-col items-center ${
        state.isShowAnswers ? "pt-[80px]" : "pt-[128px]"
      }`}
    >
      <HelmetTags
        title={`${homework ? "واجب" : "إمتحان"} | مسيو بلال عبدالله `}
      ></HelmetTags>
      <QuizHeader
        state={state}
        dispatch={dispatch}
        onFinishQuiz={onFinishQuiz}
        quizErrors={quizErrors}
        quizLoading={quizLoading}
        exam={exam}
      />

      {quizErrors && (
        <div className="flex justify-center flex-col ">
          <p>{quizErrors?.response?.data.message}</p>
          <Link
            to={`${
              exam ? `/home/exams/${ID}` : `/home/lectures/sessions/${ID}`
            }`}
            className="flex justify-center my-6"
          >
            <div className="collapse-btn top-[96px] flex h-[40px] w-[40px] cursor-pointer items-center justify-center rounded-full bg-accent text-light shadow-md shadow-black/40 duration-300 hover:scale-110 hover:text-light">
              <FontAwesomeIcon
                className={`transition-all duration-300 ease-in-out hover:scale-110 ${
                  state.collapse && "rotate-180"
                }`}
                icon={faChevronRight}
              />
            </div>
          </Link>
        </div>
      )}

      {quizLoading && <Loader />}

      {!quizLoading && !quizErrors && (
        <main
          className={`w-full min-h-full min-h-fit md:min-h-fit md:flex-col md:gap-[40px] flex max-w-[1700px] justify-center`}
        >
          {!state.isShowAnswers ? (
            <DisplayQuestion state={state}>
              <Question
                state={state}
                dispatch={dispatch}
                homework={homework}
                exam={exam}
              />
              <QuizFooter
                state={state}
                dispatch={dispatch}
                onFinishQuiz={onFinishQuiz}
                finishLoading={finishLoading}
              />
            </DisplayQuestion>
          ) : (
            <DisplayAnswers>
              <QuestionsAnswers state={state} dispatch={dispatch} exam={exam} />
              <PercentageSidebar state={state} />
            </DisplayAnswers>
          )}

          <RightQuestionsSidebar state={state} dispatch={dispatch} />
        </main>
      )}
    </section>
  );
};

export default QuizNew;
